// Web auth-flow coordinator.
//
// Thin render-side host that wires the shared RN+NativeWind step
// screens (from `@ping/ui/auth`) to web's BE clients +
// IndexedDB-backed identity. The FSM (reducer + state types) lives
// in `@ping/ui` so desktop and web walk the same transition graph;
// only this file's network glue + persistence calls are app-specific.
//
// Restyled to the new Figma flow (file gtBsWqJJcI6ZBuBv51RU35): we
// render Login (welcome), Create-account (chooseMethod), Verify code
// (enteringCode), Display name (enteringDisplayName). The FSM's older
// password / save-login / privacy / import-contacts / find-company
// steps are auto-skipped via a single effect that dispatches the
// matching "skip" action on entry.
//
// Hard rule 3: never log the user's email / phone / code / displayName.
// Only `event` discriminants pass through `log.*`.

import { useCallback, useEffect, useRef } from 'react';

import {
  type AuthenticatedUser as AuthenticatedUser,
  ChooseMethodScreen,
  detectContactKind,
  EnterCodeScreen,
  EnterDisplayNameScreen,
  pingToast,
  WelcomeScreen,
} from '@ping/ui';

import { log } from '@core/log/log';

import { useAuthFlowCoordinator,type VerifiedIdentity } from './useAuthFlowCoordinator';

export interface AuthFlowCoordinatorProps {
  /**
   * Called when the FSM reaches its terminal `done` state.
   * `wasFreshSignup` is `true` when the FSM arrived via the register
   * path (i.e. the user just completed signup on this device);
   * `false` for returning sign-ins. `recoveryBackupRequired` is the
   * server's authoritative flag from `POST /v1/auth/verify` — true
   * whenever the account has not yet uploaded its first
   * IdentityBackup, false otherwise. The host MUST gate AppShell
   * behind the recovery-key screen when the flag is true; the
   * server's `RequireBackedUp` middleware blocks every other
   * endpoint until the upload completes, so reaching AppShell
   * with the flag true would produce 403s on every request.
   */
  onAuthenticated: (
    user: AuthenticatedUser,
    opts: { wasFreshSignup: boolean; recoveryBackupRequired: boolean },
  ) => void;
  /**
   * Fires when the user picks "Already on another device? Link via QR"
   * on the welcome screen. The host (App.tsx) opens its
   * `LinkDevicePairingHost` modal which mounts
   * `LinkDevicePairingPane`. When omitted the link affordance is
   * hidden — matches the legacy single-channel sign-in.
   */
  onLinkExistingDevice?: () => void;
  /**
   * Fires when the user picks "Lost every device? Restore from backup".
   * The host opens its `RecoveryRestoreHost` which drives the OTP +
   * passphrase flow against `/v1/auth/recover/verify` +
   * `/v1/recovery/blobs`.
   */
  onRestoreFromBackup?: () => void;
}

export type { AuthenticatedUser };

export default function AuthFlowCoordinator({
  onAuthenticated,
  onLinkExistingDevice,
  onRestoreFromBackup,
}: AuthFlowCoordinatorProps) {
  const { machine, dispatch, effects } = useAuthFlowCoordinator();
  const { state, context } = machine;

  const lastVerifiedRef = useRef<VerifiedIdentity | null>(null);
  const onAuthenticatedFiredRef = useRef(false);

  // The FSM doesn't record auth-mode after `done`, so we capture it
  // when the user transitions into the register-only post-verify
  // chain. The first `acceptingPrivacy` (or any later register-only
  // step) is reached exclusively from `register`, so flipping the
  // ref there is safe. Reset on un-mount via the ref's initialiser.
  const wasFreshSignupRef = useRef(false);

  useEffect(() => {
    if (state.kind !== 'done') return;
    if (onAuthenticatedFiredRef.current) return;
    onAuthenticatedFiredRef.current = true;
    onAuthenticated(state.user, {
      wasFreshSignup: wasFreshSignupRef.current,
      // The verifyCode effect stored the verify result on
      // `lastVerifiedRef`; the BE-authoritative
      // `recoveryBackupRequired` flag rides along on the same
      // object so we can hand it to the host without re-fetching.
      // Default to `wasFreshSignup` to preserve the legacy
      // client-only signup-prompt behaviour when an older API
      // doesn't return the flag yet.
      recoveryBackupRequired:
        lastVerifiedRef.current?.recoveryBackupRequired ??
        wasFreshSignupRef.current,
    });
  }, [state, onAuthenticated]);

  const finalize = useCallback(async () => {
    const user = await effects.finalizeRegister(lastVerifiedRef.current, context.displayName ?? '');
    dispatch({ type: 'authenticated', user });
  }, [effects, context.displayName, dispatch]);

  // Auto-skip the legacy intermediate steps the Figma redesign no
  // longer surfaces. Each transition still goes through the reducer so
  // existing tests against the FSM stay green.
  useEffect(() => {
    switch (state.kind) {
      case 'enteringPassword':
        log.info({ event: 'auth.password.skip' });
        wasFreshSignupRef.current = true;
        dispatch({ type: 'passwordSet' });
        return;
      case 'enteringSaveLoginChoice':
        log.info({ event: 'auth.savelogin.skip' });
        dispatch({ type: 'saveLoginChoiceMade', saveLogin: true });
        return;
      case 'acceptingPrivacy':
        log.info({ event: 'auth.privacy.skip' });
        dispatch({ type: 'privacyAccepted' });
        return;
      case 'importingContacts':
        log.info({ event: 'auth.importcontacts.skip' });
        dispatch({ type: 'importContactsSkipped' });
        return;
      case 'findingCompany':
        log.info({ event: 'auth.findcompany.skip' });
        dispatch({ type: 'findCompanySkipped' });
        void finalize();
        return;
      default:
        return;
    }
  }, [state.kind, dispatch, finalize]);

  // Login submit: single field, route to the email or phone request
  // based on a client-side kind detection.
  const handleWelcomeSubmit = useCallback(
    async (rawInput: string) => {
      const kind = detectContactKind(rawInput);
      if (kind === 'unknown') {
        log.info({ event: 'auth.welcome.invalidContact' });
        return;
      }
      if (kind === 'email') {
        const challengeId = await effects.requestEmail(rawInput);
        if (challengeId === null) return;
        dispatch({ type: 'welcomeChoseExisting' });
        dispatch({ type: 'methodChoseEmail' });
        dispatch({ type: 'emailValidated', email: rawInput });
        dispatch({ type: 'codeRequested', challengeId });
        return;
      }
      const challengeId = await effects.requestPhone(rawInput);
      if (challengeId === null) return;
      dispatch({ type: 'welcomeChoseExisting' });
      dispatch({ type: 'methodChosePhone' });
      dispatch({ type: 'phoneValidated', e164: rawInput });
      dispatch({ type: 'codeRequested', challengeId });
    },
    [effects, dispatch],
  );

  // Sign-up submit: explicit tab-driven email vs phone.
  const handleSignupSubmit = useCallback(
    async (contact: { kind: 'email'; value: string } | { kind: 'phone'; value: string }) => {
      if (contact.kind === 'email') {
        const challengeId = await effects.requestEmail(contact.value);
        if (challengeId === null) return;
        dispatch({ type: 'methodChoseEmail' });
        dispatch({ type: 'emailValidated', email: contact.value });
        dispatch({ type: 'codeRequested', challengeId });
        return;
      }
      const challengeId = await effects.requestPhone(contact.value);
      if (challengeId === null) return;
      dispatch({ type: 'methodChosePhone' });
      dispatch({ type: 'phoneValidated', e164: contact.value });
      dispatch({ type: 'codeRequested', challengeId });
    },
    [effects, dispatch],
  );

  const handleCodeSubmit = useCallback(
    async (code: string) => {
      if (state.kind !== 'enteringCode') return;
      const challengeId = context.challengeId;
      if (challengeId === null) {
        log.warn({ event: 'auth.flow.codeWithoutChallenge' });
        // Toast surfaces the error; state machine stays on the OTP
        // step so the user can re-request a code via "Resend".
        pingToast.error({
          title: 'Session expired',
          description: 'Please request a new code and try again.',
        });
        return;
      }
      const verified = await effects.verifyCode(
        code,
        state.contact,
        challengeId,
        context.displayName,
      );
      if (verified === null) return;
      lastVerifiedRef.current = verified;
      log.info({ event: 'auth.code.verified' });
      dispatch({ type: 'codeVerified' });
      if (context.authMode === 'signIn') {
        const user: AuthenticatedUser = {
          tokens: { hasAccessToken: true },
          identity: {
            accountId: verified.accountId,
            deviceId: verified.deviceId,
            displayName: verified.displayName,
          },
        };
        dispatch({ type: 'authenticated', user });
      }
    },
    [state, context, effects, dispatch],
  );

  const handleResend = useCallback(async () => {
    if (state.kind !== 'enteringCode') return;
    log.info({ event: 'auth.code.resend' });
    const challengeId =
      state.contact.kind === 'email'
        ? await effects.requestEmail(state.contact.value)
        : await effects.requestPhone(state.contact.e164);
    if (challengeId === null) return;
    dispatch({ type: 'codeRequested', challengeId });
  }, [state, effects, dispatch]);

  const handleDisplayName = useCallback(
    (displayName: string) => {
      log.info({ event: 'auth.displayname.set' });
      dispatch({ type: 'displayNameSet', displayName });
    },
    [dispatch],
  );

  switch (state.kind) {
    case 'welcome':
      return (
        <WelcomeScreen
          onSubmit={(input) => {
            void handleWelcomeSubmit(input);
          }}
          onChooseNew={() => {
            log.info({ event: 'auth.welcome.new' });
            dispatch({ type: 'welcomeChoseNew' });
          }}
          {...(onLinkExistingDevice !== undefined
            ? { onLinkExistingDevice }
            : {})}
          {...(onRestoreFromBackup !== undefined
            ? { onRestoreFromBackup }
            : {})}
        />
      );

    case 'chooseMethod':
      return (
        <ChooseMethodScreen
          onSubmit={(contact) => {
            void handleSignupSubmit(contact);
          }}
          onChooseExisting={() => {
            log.info({ event: 'auth.signup.existing' });
            dispatch({ type: 'back' });
          }}
        />
      );

    case 'enteringEmail':
    case 'enteringPhone':
      // These intermediate states fire only for a single tick between
      // dispatches in the new flow — rendering nothing avoids a flash
      // of the legacy single-field screens.
      return null;

    case 'enteringCode':
      return (
        <EnterCodeScreen
          contact={state.contact}
          onSubmit={(code) => {
            void handleCodeSubmit(code);
          }}
          onResend={() => {
            void handleResend();
          }}
          onBack={() => {
            // Return to the welcome (contact-entry) screen so the user can fix a
            // mistyped email/phone. The intermediate `enteringEmail`/`enteringPhone`
            // states render nothing, so step back through them to `welcome`
            // (matches the desktop coordinator). Extra backs at `welcome` are
            // no-ops.
            dispatch({ type: 'back' });
            dispatch({ type: 'back' });
            dispatch({ type: 'back' });
          }}
        />
      );

    case 'enteringPassword':
    case 'enteringSaveLoginChoice':
    case 'acceptingPrivacy':
    case 'importingContacts':
    case 'findingCompany':
      // Skipped by the auto-advance effect above. Render nothing
      // while the matching dispatch lands on the next state.
      return null;

    case 'enteringDisplayName':
      return <EnterDisplayNameScreen onSubmit={handleDisplayName} />;

    case 'done':
      return null;

    default: {
      const _exhaustive: never = state;
      void _exhaustive;
      return null;
    }
  }
}
